import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, SectionCard, Empty } from "@/components/shared";
import { num } from "@/lib/format";
import { SlidersHorizontal, Plus } from "lucide-react";
import { toast } from "sonner";

const SECTIONS = [
  { key: "routing_rules", label: "Lead Routing" },
  { key: "catalogue", label: "Reward Catalogue" },
  { key: "reason_codes", label: "Reason Codes" },
  { key: "categories", label: "Project Categories" },
  { key: "territories", label: "Territories" },
  { key: "roles", label: "Roles & Permissions" },
];

export default function Studio() {
  const [sec, setSec] = useState("routing_rules");
  const [data, setData] = useState(null);

  const load = (k) => api.get(`/studio/config/${k}`).then((r) => setData(r.data));
  useEffect(() => { load(sec); }, [sec]);

  return (
    <div>
      <PageHeader title="DIY Program Studio" subtitle="Admin-configurable rules, catalogues, routing, roles and reason codes." testid="program-studio-admin-config" />
      <div className="grid lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <div className="card-surface p-2">
            {SECTIONS.map((s) => (
              <button key={s.key} data-testid={`studio-${s.key}`} onClick={() => setSec(s.key)} className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 ${sec === s.key ? "bg-orange-50 text-slate-900" : "text-slate-600 hover:bg-slate-50"}`}>
                <SlidersHorizontal size={14} className={sec === s.key ? "text-[var(--copper)]" : "text-slate-400"} /> {s.label}
              </button>
            ))}
          </div>
        </div>
        <div className="lg:col-span-3">
          <SectionCard title={SECTIONS.find((s) => s.key === sec)?.label} testid="studio-panel">
            {!data ? <Empty text="Loading…" /> : <StudioTable sec={sec} data={data} reload={() => load(sec)} />}
          </SectionCard>
        </div>
      </div>
    </div>
  );
}

function StudioTable({ sec, data, reload }) {
  if (sec === "routing_rules") {
    const rows = Array.isArray(data) ? data : [];
    return (
      <>
        <table className="w-full text-sm mb-4">
          <thead><tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-100"><th className="py-2">Rule</th><th className="py-2">City</th><th className="py-2">Assignee</th><th className="py-2">SLA</th></tr></thead>
          <tbody>{rows.map((r) => <tr key={r.id} className="border-b border-slate-50"><td className="py-2.5">{r.name}</td><td className="py-2.5 text-slate-600">{r.city}</td><td className="py-2.5 text-slate-600">{r.assignee}</td><td className="py-2.5 font-mono">{r.sla_hours}h</td></tr>)}</tbody>
        </table>
        <AddRouting reload={reload} />
      </>
    );
  }
  if (sec === "catalogue") {
    const rows = Array.isArray(data) ? data : [];
    return (
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-100"><th className="py-2">Reward</th><th className="py-2">Category</th><th className="py-2">Points</th><th className="py-2">Stock</th></tr></thead>
        <tbody>{rows.map((r) => <tr key={r.id} className="border-b border-slate-50"><td className="py-2.5">{r.name}</td><td className="py-2.5 text-slate-600">{r.category}</td><td className="py-2.5 font-mono">{num(r.points)}</td><td className="py-2.5 font-mono">{r.stock}</td></tr>)}</tbody>
      </table>
    );
  }
  // generic item list
  const items = data.items || [];
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((it, i) => (
        <span key={i} className="px-3 py-1.5 rounded-lg bg-slate-100 text-sm text-slate-700 border border-slate-200">{it.label || it}</span>
      ))}
      {items.length === 0 && <Empty />}
    </div>
  );
}

function AddRouting({ reload }) {
  const [f, setF] = useState({ name: "", city: "Mumbai", assignee: "", sla_hours: 48 });
  const add = async () => {
    if (!f.name) return;
    try { await api.post("/studio/routing_rules", f); toast.success("Rule added"); reload(); setF({ name: "", city: "Mumbai", assignee: "", sla_hours: 48 }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-100">
      <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Rule name" className="border border-slate-300 rounded-lg px-3 py-2 text-sm flex-1 min-w-[120px]" data-testid="routing-name" />
      <select value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">{["Mumbai", "Delhi NCR", "Bengaluru", "Hyderabad", "Chennai", "Pune", "Ahmedabad", "Kolkata"].map((c) => <option key={c}>{c}</option>)}</select>
      <input value={f.assignee} onChange={(e) => setF({ ...f, assignee: e.target.value })} placeholder="Assignee" className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
      <button onClick={add} data-testid="add-routing" className="flex items-center gap-1.5 text-white font-semibold px-4 py-2 rounded-lg" style={{ background: "var(--copper)" }}><Plus size={15} /> Add</button>
    </div>
  );
}
